import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';

import { DateTimeRange } from './DateTime/DateTimeRangePicker';
import Loading from './Loading';
import TimeseriesChart from './TimeseriesChart/TimeseriesChart';
import { chartColors } from '../styles/colors';
import { getTimeseries } from '../services/timeseries';
import SnackConfig from '../config/snackConfig';
import SnackbarAlert from './SnackbarAlert';

const useStyles = makeStyles({
  container: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
});
interface MetricProps {
  primaryMetricSelectors: string;
  secondaryMetricSelectors?: string;
  primaryName: string;
  secondaryName?: string;
  columnDefs: string[];
  primaryYLabel?: string;
  secondaryYLabel?: string;
  timeRange: DateTimeRange;
  isRelative?: boolean;
}

const Metric: React.FC<MetricProps> = ({
  primaryMetricSelectors,
  secondaryMetricSelectors = '',
  primaryName,
  secondaryName = '',
  columnDefs,
  primaryYLabel = '',
  secondaryYLabel = '',
  timeRange,
  isRelative = false,
}: MetricProps) => {
  const [primaryDatapointsData, setPrimaryDatapointsData] = useState<(number | null)[][]>();
  const [secondaryDatapointsData, setSecondaryDatapointsData] = useState<(number | null)[][]>();
  const [chartTimeRange, setChartTimeRange] = useState<DateTimeRange>(timeRange);
  const [autoRefreshDuration] = useState(moment.duration(1, 'm'));
  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));
  const [running, setRunning] = useState(true);
  useEffect(() => {
    setRunning(true);
    const fetchData = async () => {
      const primaryDatapoints = await getTimeseries(
        chartTimeRange.start.format(),
        chartTimeRange.end.format(),
        primaryName,
        JSON.parse(primaryMetricSelectors)
      );
      if (primaryDatapoints !== null) {
        setPrimaryDatapointsData(primaryDatapoints);
      } else {
        setSnackConfig(
          new SnackConfig({
            open: true,
            message: 'There was an error while fetching data.',
          })
        );
      }
      if (secondaryName !== '' || secondaryMetricSelectors.length > 0) {
        const secondaryDatapoints = await getTimeseries(
          chartTimeRange.start.format(),
          chartTimeRange.end.format(),
          secondaryName,
          JSON.parse(secondaryMetricSelectors)
        );
        if (secondaryDatapoints !== null) {
          setSecondaryDatapointsData(secondaryDatapoints);
        } else {
          setSnackConfig(
            new SnackConfig({
              open: true,
              message: 'There was an error while fetching data.',
            })
          );
        }
      } else {
        setSecondaryDatapointsData([]);
      }
      setRunning(false);
    };
    void fetchData();

    let timerId: NodeJS.Timeout | null = null;
    if (isRelative) {
      timerId = setTimeout(() => {
        setChartTimeRange({
          start: moment(chartTimeRange.start.clone()).add(autoRefreshDuration),
          end: moment(chartTimeRange.end.clone()).add(autoRefreshDuration),
        });
      }, autoRefreshDuration.asMilliseconds());
    }

    return () => {
      if (timerId != null) {
        clearTimeout(timerId);
      }
    };
  }, [
    primaryMetricSelectors,
    secondaryMetricSelectors,
    chartTimeRange,
    isRelative,
    autoRefreshDuration,
    primaryName,
    secondaryName,
  ]);

  const styles = useStyles();

  return (
    <div className={styles.container}>
      {running && <Loading />}
      {!running && typeof primaryDatapointsData !== 'undefined' && typeof secondaryDatapointsData !== 'undefined' && (
        <TimeseriesChart
          primaryName={secondaryName !== '' ? 'Issue' : primaryName}
          secondaryName={secondaryName !== '' ? 'Candidate' : secondaryName}
          primaryYLabel={primaryYLabel}
          secondaryYLabel={secondaryYLabel}
          primaryData={primaryDatapointsData}
          secondaryData={secondaryDatapointsData}
          columnDefs={columnDefs}
          primaryColors={
            secondaryDatapointsData.length > 0
              ? ['', chartColors[3], chartColors[2]]
              : ['', chartColors[1], chartColors[0]]
          }
          secondaryColors={['', chartColors[1], chartColors[0]]}
        />
      )}
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default Metric;
